import { Box, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useInterval } from 'usehooks-ts';
import Colors from '../../utils/color';
import { ASSIST_COUNTDOWN_TICK_MS, remainingSeconds } from './AssistCountdown';

const RING_SIZE = 56;

/** Fraction of the offer's time left at or under which the ring turns amber, then red. */
export const RING_AMBER_AT = 0.5;
export const RING_RED_AT = 0.2;

export const ringColor = (fractionLeft: number) => {
  if (fractionLeft <= RING_RED_AT) return Colors.amaranth;
  if (fractionLeft <= RING_AMBER_AT) return Colors.amber;
  return Colors.green;
};

interface Props {
  /** Local epoch ms the offer arrived; the ring's full sweep. */
  receivedAt: number;
  /** Local epoch ms; never a server time (see `remainingSeconds`). */
  deadlineAt: number;
}

/**
 * The offer's countdown: a ring that drains from receipt to the local deadline,
 * with the seconds inside. Built the way `FlagWindowDial` is — a full track
 * under a determinate arc — so the two countdowns on the lobby read alike.
 */
const AssistClaimRing = ({ receivedAt, deadlineAt }: Props) => {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), ASSIST_COUNTDOWN_TICK_MS);
  const seconds = remainingSeconds(deadlineAt, now);
  const total = Math.max(1, deadlineAt - receivedAt);
  const fractionLeft = Math.max(0, Math.min(1, (deadlineAt - now) / total));

  return (
    <Box
      role="timer"
      aria-label={`${seconds} seconds left to claim`}
      data-testid="assist-countdown"
      sx={{
        position: 'relative',
        width: RING_SIZE,
        height: RING_SIZE,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={RING_SIZE}
        thickness={3}
        sx={{
          position: 'absolute',
          color: 'rgba(255,255,255,0.18)',
          pointerEvents: 'none',
        }}
      />
      <CircularProgress
        variant="determinate"
        value={fractionLeft * 100}
        size={RING_SIZE}
        thickness={3}
        sx={{
          position: 'absolute',
          color: ringColor(fractionLeft),
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'relative',
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1,
          color: Colors.white,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {seconds}s
      </Box>
    </Box>
  );
};

export default React.memo(AssistClaimRing);
